import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import {Button} from '../../../components/ui/Button';
import { formatCurrency, formatDate } from '../../../utils/localization';

const EventCard = ({ event }) => {

  const getStatusColor = (status) => {
    switch (status) {
      case 'settled':
        return 'text-success bg-success/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'active':
        return 'text-primary bg-primary/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'settled':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'active':
        return 'Activity';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 transition-all duration-200 hover:shadow-elevated hover:border-primary/20 group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
            {event?.name}
          </h3>
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} />
              <span>{formatDate(event?.date)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Users" size={14} />
              <span>{event?.participantCount} participants</span>
            </div>
          </div>
        </div>
        <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(event?.status)}`}>
          <Icon name={getStatusIcon(event?.status)} size={12} />
          <span className="capitalize">{event?.status}</span>
        </div>
      </div>
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">Total Expenses</span>
          <span className="text-lg font-semibold text-foreground">
            {formatCurrency(event?.totalExpenses)}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min((event?.totalExpenses / event?.budget) * 100, 100)}%` }}
          />
        </div>
        {event?.budget && (
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>Budget: {formatCurrency(event?.budget)}</span>
            <span>{Math.round((event?.totalExpenses / event?.budget) * 100)}%</span>
          </div>
        )}
      </div>
      <div className="mb-4">
        <div className="text-sm text-muted-foreground mb-2">Recent Activity</div>
        <div className="space-y-1">
          {event?.recentExpenses?.slice(0, 2)?.map((expense, index) => (
            <div key={index} className="flex items-center justify-between text-sm">
              <span className="text-foreground truncate flex-1 mr-2">{expense?.description}</span>
              <span className="text-muted-foreground font-medium">
                {formatCurrency(expense?.amount)}
              </span>
            </div>
          ))}
          {event?.recentExpenses?.length > 2 && (
            <div className="text-xs text-muted-foreground">
              +{event?.recentExpenses?.length - 2} more expenses
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="flex -space-x-2">
            {event?.participants?.slice(0, 3)?.map((participant, index) => (
              <div
                key={index}
                className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-xs font-medium text-primary-foreground border-2 border-card"
                title={participant?.name}
              >
                {participant?.name?.charAt(0)?.toUpperCase()}
              </div>
            ))}
            {event?.participants?.length > 3 && (
              <div className="w-6 h-6 bg-muted rounded-full flex items-center justify-center text-xs font-medium text-muted-foreground border-2 border-card">
                +{event?.participants?.length - 3}
              </div>
            )}
          </div>
        </div>
        <Link to={`/events/${event?.id}`}>
          <Button variant="outline" size="sm" iconName="ArrowRight" iconPosition="right">
            View Details
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default EventCard;
